import { useState, useEffect, type ReactNode } from 'react';
import { Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, ShieldX, ArrowLeft } from 'lucide-react';
import { ChAuthProvider, useChAuth } from './contexts/ChAuthContext';
import { ChLayout } from './components/ChLayout';
import { ChDashboard } from './pages/ChDashboard';
import { ChProjectsList } from './pages/ChProjectsList';
import { ChProjectForm } from './pages/ChProjectForm';
import { ChProjectDetail } from './pages/ChProjectDetail';
import { ChUsersPage } from './pages/ChUsersPage';
import { ClientReviewPage } from './pages/ClientReviewPage';
import { ChNotificationsPage } from './pages/ChNotificationsPage';
import { PresentationPage } from './pages/PresentationPage';
import { chSupabase } from './lib/supabase';
import './styles/design-system.css';

function ChNoAccessScreen() {
  const navigate = useNavigate();
  return (
    <div className="ch-app min-h-screen flex items-center justify-center p-6">
      <div className="text-center max-w-sm ch-animate-in">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-100 mb-5">
          <ShieldX className="w-7 h-7 text-gray-400" />
        </div>
        <h1 className="text-lg font-bold text-[var(--ch-ink)] mb-2">Sem acesso ao Content Hub</h1>
        <p className="text-sm text-gray-500 mb-6">
          A tua conta ainda não tem acesso ao Content Hub. Pede a um administrador para te dar acesso.
        </p>
        <button
          onClick={() => navigate('/apps')}
          className="ch-btn ch-btn-primary"
        >
          Voltar
        </button>
      </div>
    </div>
  );
}

function ClientProjectPicker() {
  const { user, signOut } = useChAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // "Projetos" from a review page opens the list even with one project
  const autoOpen = searchParams.get('escolher') !== '1';
  const [projects, setProjects] = useState<{ id: string; slug: string; name: string; logo_url: string | null }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: links } = await chSupabase
        .from('ch_user_projects')
        .select('project_id')
        .eq('user_id', user.id);

      if (!links || links.length === 0) {
        setLoading(false);
        return;
      }

      const projectIds = links.map((l: { project_id: string }) => l.project_id);
      const { data: projectsData } = await chSupabase
        .from('ch_projects')
        .select('id, slug, name')
        .in('id', projectIds);

      if (projectsData) {
        const withBranding = await Promise.all(
          projectsData.map(async (p: { id: string; slug: string; name: string }) => {
            const { data: branding } = await chSupabase
              .from('ch_branding')
              .select('logo_url')
              .eq('project_id', p.id)
              .maybeSingle();
            return { ...p, logo_url: branding?.logo_url || null };
          })
        );
        setProjects(withBranding);

        if (withBranding.length === 1 && autoOpen) {
          navigate(`/contenthub/projeto/${withBranding[0].slug}/review`, { replace: true });
          return;
        }
      }
      setLoading(false);
    })();
  }, [user, navigate, autoOpen]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="text-center max-w-sm">
          <p className="text-sm text-gray-500">Não tens projetos atribuídos de momento.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 flex flex-col items-center justify-center">
      <h1 className="text-lg font-bold text-gray-900 mb-6">Escolhe o projeto</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-md">
        {projects.map(p => (
          <button
            key={p.id}
            onClick={() => navigate(`/contenthub/projeto/${p.slug}/review`)}
            className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all text-left"
          >
            {p.logo_url ? (
              <img src={p.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-500">
                {p.name.charAt(0)}
              </div>
            )}
            <span className="text-sm font-semibold text-gray-900 truncate">{p.name}</span>
          </button>
        ))}
      </div>
      <button
        onClick={async () => { await signOut(); navigate('/login'); }}
        className="mt-8 text-xs font-medium text-gray-400 hover:text-gray-700"
      >
        Terminar sessão
      </button>
      <p className="text-[10px] uppercase tracking-widest text-gray-300 mt-6">Powered by MOHIT</p>
    </div>
  );
}

// Clients only ever see their projects' review pages. Keeping them out of
// the staff routes also stops the picker from re-rendering over the review
// page it just opened.
function ClientNotifications() {
  const navigate = useNavigate();
  return (
    <div className="ch-app min-h-screen">
      <div className="max-w-3xl mx-auto pt-4 px-4">
        <button onClick={() => navigate(-1)} className="ch-btn ch-btn-ghost ch-btn-sm">
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </button>
      </div>
      <div className="max-w-3xl mx-auto">
        <ChNotificationsPage />
      </div>
    </div>
  );
}

function ClientRoutes() {
  return (
    <Routes>
      <Route path="projeto/:slug/review" element={<ClientReviewPage />} />
      <Route path="notificacoes" element={<ClientNotifications />} />
      <Route path="*" element={<ClientProjectPicker />} />
    </Routes>
  );
}

function ChAccessGate({ staff, client }: { staff: ReactNode; client: ReactNode }) {
  const { user, loading, accessInfo } = useChAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user && !accessInfo.hasAccess) {
      navigate('/login', { replace: true });
    }
  }, [loading, user, accessInfo, navigate]);

  if (loading) {
    return (
      <div className="ch-app min-h-screen flex items-center justify-center">
        <div className="text-center ch-animate-in">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 text-[var(--ch-ink)] animate-spin" />
          <p className="text-sm text-gray-500">A carregar...</p>
        </div>
      </div>
    );
  }

  if (!user && !accessInfo.hasAccess) {
    return null;
  }

  if (!accessInfo.hasAccess) {
    return <ChNoAccessScreen />;
  }

  if (accessInfo.isAgencyStaff) {
    return <>{staff}</>;
  }

  if (accessInfo.role === 'client' || accessInfo.role === 'talent') {
    return <>{client}</>;
  }

  return <ChNoAccessScreen />;
}

function ChAdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useChAuth();
  if (user?.role !== 'admin') return <Navigate to="/contenthub" replace />;
  return <>{children}</>;
}

function ContentHubRoutes() {
  return (
    <Routes>
      <Route path="projeto/:slug/apresentacao" element={<PresentationPage />} />
      <Route path="*" element={
        <ChAccessGate client={<ClientRoutes />} staff={
          <ChLayout>
            <Routes>
              <Route index element={<ChDashboard />} />
              <Route path="projetos" element={<ChProjectsList />} />
              <Route path="projetos/novo" element={<ChProjectForm />} />
              <Route path="projetos/:slug" element={<ChProjectDetail />} />
              <Route path="projeto/:slug/review" element={<ClientReviewPage />} />
              <Route path="projetos/:slug/editar" element={<ChProjectForm />} />
              <Route path="utilizadores" element={<ChAdminRoute><ChUsersPage /></ChAdminRoute>} />
              <Route path="notificacoes" element={<ChNotificationsPage />} />
              <Route path="*" element={<Navigate to="/contenthub" replace />} />
            </Routes>
          </ChLayout>
        } />
      } />
    </Routes>
  );
}

export function ContentHubApp() {
  return (
    <ChAuthProvider>
      <ContentHubRoutes />
    </ChAuthProvider>
  );
}
